import { useEffect } from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import { ExpensablesDataTableDocument } from 'generated/resources/datatable.graphql'
import {
  ApprovalsMaterializedApprovalFlowHistoryDocument,
  GetExpensablesDocument,
  GetExpensesCountDocument,
  RequestChangesDocument,
} from 'generated/resources/graphql'
import { last, sortBy } from 'lodash'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import type StepApprover from 'modules/approvals/store/models/StepApprover'
import {
  ConfirmationModal as Modal,
  useBulkActions,
  useConfirmationModal,
} from 'modules/expenses/components/expenses/Detail/hooks/useBulkActions'
import { buildErrorMessages } from 'modules/expenses/components/expenses/Form/Buttons/helpers/ActionErrorMessageHandler'
import ReviewModal from 'modules/expenses/components/expenses/ReviewModal'
import { Expensable } from 'modules/expenses/types/Expensable'

interface RejectButtonProps {
  expensable: Expensable
  stepApprovers: StepApprover[]
  canRequestChanges: boolean
  canOverrideApprovalFlow: boolean
  onLoadingChange?: (loading: boolean) => void
  disabled?: boolean
}

const isLastMaterializedStep = ({
  currentStepId,
  stepApprovers,
}: {
  currentStepId?: number | null
  stepApprovers: StepApprover[]
}) => {
  const lastStep = last(sortBy(stepApprovers, [(step) => step.get('approval_step_position')]))
  if (!lastStep) return false

  return lastStep.id === currentStepId
}

const RejectExpensableButton = ({
  expensable,
  stepApprovers,
  canRequestChanges,
  canOverrideApprovalFlow,
  onLoadingChange,
  disabled,
}: RejectButtonProps) => {
  const [requestChanges, { loading }] = useMutation(RequestChangesDocument, {
    extractorFn: (data) => data.expensesMutations.requestChangesExpensesExpensable,
    refetchQueries: [
      ApprovalsMaterializedApprovalFlowHistoryDocument,
      GetExpensablesDocument,
      ExpensablesDataTableDocument,
      GetExpensesCountDocument,
    ],
    awaitRefetchQueries: true,
  })

  useEffect(() => {
    onLoadingChange?.(loading)
  }, [loading, onLoadingChange])

  const bulkActions = useBulkActions()

  const { closeModal, modal } = useConfirmationModal()

  const isLastStep = isLastMaterializedStep({
    currentStepId: expensable.approvalStepApproverId,
    stepApprovers,
  })

  const canReviewStep = expensable.currentUserApprovalStatus == 'pending'

  const canGlobalApprove = canOverrideApprovalFlow && !isLastStep
  const isGlobalReject = canGlobalApprove && !canReviewStep

  const handleReject = isGlobalReject ? bulkActions.globalReject : bulkActions.reject

  const handleRequestChanges = async () => {
    bulkActions.requestChanges()
  }

  const confirmRequestChanges = async (reason: string) => {
    const result = await requestChanges({
      variables: {
        id: expensable.id,
        reason: reason,
      },
    })

    const handled = handleResult(result, (errors) => {
      const errorMessages = buildErrorMessages(errors)
      app.setFlash({
        type: 'wrong',
        message: errorMessages || i18n.t('errors.generic_error'),
        time: 3000,
      })
    })
    if (!handled.success) return handled
    const changesRequested = handled.data?.expensable
    if (!changesRequested) return handled
    app.setFlash({
      type: 'correct',
      message: i18n.t('expenses_v2.flash.request_changes.success'),
    })
    closeModal()
  }

  if (modal === Modal.RequestChanges) {
    return (
      <ReviewModal
        type="request_changes"
        onSubmit={(reason) => confirmRequestChanges(reason ?? '')}
        onClose={closeModal}
        spender={expensable.owner?.fullName ?? ''}
      />
    )
  }

  if (canRequestChanges) {
    return (
      <Button.Split
        type="primary"
        label={i18n.t('expenses_v2.buttons.request_changes')}
        onClick={handleRequestChanges}
        disabled={bulkActions.submitting || loading || disabled}
      >
        <Button.Split.Option
          onClick={handleReject}
          disabled={bulkActions.submitting || loading || disabled}
        >
          {i18n.t('expenses_v2.buttons.reject')}
        </Button.Split.Option>
      </Button.Split>
    )
  }
  return (
    <Button
      type="primary"
      onClick={handleReject}
      disabled={bulkActions.submitting || loading || disabled}
      loading={loading}
      iconName={loading ? 'upload' : undefined}
    >
      {i18n.t('expenses_v2.buttons.reject')}
    </Button>
  )
}

export default RejectExpensableButton
